import { create } from 'zustand';
import { onboardingAPI } from '../api/onboarding';

interface OnboardingState {
  isCompleted: boolean;
  currentStep: number;
  isLoading: boolean;
  isTourRunning: boolean;

  // Actions
  loadStatus: () => Promise<void>;
  markComplete: () => Promise<void>;
  markSkipped: () => Promise<void>;
  updateStep: (step: number) => Promise<void>;
  reset: () => Promise<void>;
  setTourRunning: (running: boolean) => void;
}

export const useOnboardingStore = create<OnboardingState>((set) => ({
  isCompleted: false,
  currentStep: 0,
  isLoading: false,
  isTourRunning: false,

  loadStatus: async () => {
    set({ isLoading: true });
    try {
      const status = await onboardingAPI.getStatus();
      if (status) {
        set({
          isCompleted: status.isCompleted,
          currentStep: status.currentStep || 0,
          isLoading: false,
        });
      } else {
        // If no status (new user), create new one
        // Backend upsert will handle this
        set({ isCompleted: false, currentStep: 0, isLoading: false });
      }
    } catch (error) {
      console.error('Failed to load onboarding status:', error);
      // Silently fail; don't show tour if API is down (safety first)
      set({ isCompleted: true, isLoading: false });
    }
  },

  markComplete: async () => {
    try {
      await onboardingAPI.markComplete();
      set({ isCompleted: true });
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
  },

  markSkipped: async () => {
    try {
      await onboardingAPI.markSkipped();
      set({ isCompleted: true }); // Skipped counts as "no longer show"
    } catch (error) {
      console.error('Failed to mark onboarding skipped:', error);
    }
  },

  updateStep: async (step: number) => {
    set({ currentStep: step });
    try {
      await onboardingAPI.updateStep(step);
    } catch (error) {
      console.error('Failed to update onboarding step:', error);
    }
  },

  reset: async () => {
    try {
      await onboardingAPI.reset();
      set({ isCompleted: false, currentStep: 0 });
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
    }
  },

  setTourRunning: (running: boolean) => {
    set({ isTourRunning: running });
  },
}));
